import React from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { formatMoney } from 'accounting-js';

import Typography from '../../atoms/typography';

const ListWrapper = styled(Typography)`
  color: ${(props) => props.theme.color[props.color ?? 'brand']};
  display: flex;

  dt {
    min-width: 5rem;
    padding-right: 1rem;
    flex: 1
  }
`;

const HR = styled.hr`
  border: 0;
  border-top: 1px solid ${(props) => props.theme.color.brand};
  margin: 1rem 0;
`;

const Total = styled(Typography)`
  flex: 1;
  letter-spacing: 0.2em;
  min-width: 5rem;
  padding-right: 1rem;
  text-transform: uppercase;
`;

const FreeShipping = styled(Typography)`
  color: ${(props) => props.theme.color.brandA11yRed};
  letter-spacing: 0.1em;
  margin-right: -0.1em;
  min-width: 5rem;
  text-transform: uppercase;
  display: block;
`;

const OrderSummary = (props) => (
  <dl className={props.className}>
    <ListWrapper element="div" like="dec-1">
      <dt>Subtotal</dt>
      <dd data-test-id="order-subtotal">{formatMoney(props.subtotal, { precision: 2 })}</dd>
    </ListWrapper>

    <ListWrapper element="div" like="dec-1">
      <dt>Shipping</dt>
      <dd data-test-id="order-shipping-total">
        {
          ['delivery', 'payment', 'confirm'].includes(props.state)
            ? (
              <>
                {
                  props.shipping > 0
                    ? formatMoney(props.shipping, { precision: 2 })
                    : <FreeShipping element="span" like="label-1">Free Shipping</FreeShipping>
                }
              </>
            )
            : 'Shipping Calculated at Checkout'
        }
      </dd>
    </ListWrapper>

    {
      props.gift > 0 && (
        <ListWrapper element="div" like="dec-1">
          <dt>Gift wrap</dt>
          <dd data-test-id="order-gift-wrap-total">{formatMoney(props.gift, { precision: 2 })}</dd>
        </ListWrapper>
      )
    }

    {
      props.miscellaneous.length > 0 && props.miscellaneous.map((miscellaneous) => (
        <ListWrapper color="brandGreen" key={`miscellaneous-${miscellaneous.label}`} element="div" like="dec-1">
          <dt>{miscellaneous.label}</dt>
          <dd>
            -
            {formatMoney(miscellaneous.amount * -1, { precision: 2 })}
          </dd>
        </ListWrapper>
      ))
    }

    {
      props?.promotions?.length > 0 && props?.promotions?.map((promotions, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <ListWrapper color="brandGreen" key={`promotions-${promotions.label}-${i}`} element="div" like="dec-1">
          <dt>{promotions.label}</dt>
          <dd>
            -
            {formatMoney(promotions.total * -1, { precision: 2 })}
          </dd>
        </ListWrapper>
      ))
    }

    {
      props.tax.length > 0 && (
        <ListWrapper key="tax" element="div" like="dec-1">
          <dt>Taxes</dt>
          <dd data-test-id="order-tax-total">
            {
              formatMoney(
                props.tax.reduce((a, c) => a + parseFloat(c.amount), 0),
                { precision: 2 }
              )
            }
          </dd>
        </ListWrapper>
      )
    }

    <HR />

    <ListWrapper element="div" like="dec-1">
      <Total element="p" like="label-1">Total</Total>
      <dd data-test-id="order-total">{formatMoney(props.total, { precision: 2 })}</dd>
    </ListWrapper>
  </dl>
);

OrderSummary.defaultProps = {
  className: '',
  state: 'address',
  promotions: []
};

OrderSummary.propTypes = {
  className: PropTypes.string,
  gift: PropTypes.number.isRequired,
  miscellaneous: PropTypes.array.isRequired,
  // eslint-disable-next-line react/no-unused-prop-types
  promotions: PropTypes.array,
  shipping: PropTypes.string.isRequired,
  subtotal: PropTypes.string.isRequired,
  tax: PropTypes.array.isRequired,
  state: PropTypes.oneOf(['cart', 'address', 'delivery', 'payment', 'confirm', 'complete']),
  total: PropTypes.string.isRequired
};

OrderSummary.whyDidYouRender = true;

export default OrderSummary;
